import { ApiError } from "@lumora/bridge";
import { ViewModel } from "../mvvm/ViewModel.js";
const UNCONFIGURED_NOTICE = "Platform Stripe billing is not configured on this server (LUMORA_STRIPE_SECRET_KEY is unset). Configure platform Stripe keys to enable hosting subscriptions and billing setup.";
function formatBillingError(err, fallback) {
    if (err instanceof ApiError && err.code === "billing_disabled") {
        return UNCONFIGURED_NOTICE;
    }
    if (err instanceof Error) {
        if (err.message.trim().toLowerCase() === "billing is not configured") {
            return UNCONFIGURED_NOTICE;
        }
        if (err.message.trim())
            return err.message;
    }
    return fallback;
}
/**
 * BillingViewModel handles the Stripe billing-portal redirect.
 *
 * The `redirect` function is injected so it can be swapped for a test spy
 * (never call window.location directly from a ViewModel).
 */
export class BillingViewModel extends ViewModel {
    client;
    redirect;
    constructor(client, redirect) {
        super({ loading: false, statusLoading: false, error: null, status: null });
        this.client = client;
        this.redirect = redirect;
    }
    /** False only when the backend explicitly reports platform Stripe is unset. */
    get isBillingConfigured() {
        return this.state.status?.billingConfigured !== false;
    }
    /**
     * Actionable notice when running against a local/memory API without
     * platform Stripe keys, preventing a fake "pay hosting first" loop (#901).
     */
    unconfiguredNotice() {
        if (this.isBillingConfigured)
            return null;
        return UNCONFIGURED_NOTICE;
    }
    async loadStatus() {
        await this.run(async () => {
            const status = await this.client.getBillingStatus();
            this.setState({ status });
        }, "Failed to load billing status.", { flag: "statusLoading" });
    }
    /**
     * Plain-language notice when hosting is still on an offline waiver and no
     * Stripe subscription is on file. Null when the status does not say so.
     */
    waiverNotice() {
        const status = this.state.status;
        if (!status?.hostingWaivedUntil || status.hasStripeSubscription !== false)
            return null;
        const ends = new Date(status.hostingWaivedUntil);
        if (Number.isNaN(ends.getTime()))
            return null;
        const when = ends.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
        if (ends.getTime() <= Date.now()) {
            return `The complimentary hosting period ended on ${when}. Add a subscription to keep online ordering available.`;
        }
        return `Hosting is included through ${when}. Add a subscription before that date so online ordering stays available.`;
    }
    async openPortal(returnUrl) {
        await this.run(async () => {
            const { url } = await this.client.createBillingPortalSession(returnUrl);
            this.redirect(url);
        }, "Failed to open billing portal.", {
            onError: (err) => ({
                error: formatBillingError(err, "Failed to open billing portal."),
            }),
        });
    }
}
//# sourceMappingURL=BillingViewModel.js.map